import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { DocumentCenterPrivacyType } from "@/enums/DocumentCenterPrivacyType";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { DocumentCenterFile } from "@/models/DocumentCenterFile";
import { DocumentCenterFileSmall } from "@/models/DocumentCenterFileSmall";
import { DocumentCenterNode } from "@/models/DocumentCenterNode";
import { DocumentCenterPathStack } from "@/models/DocumentCenterPathStack";
import { UserPersonalFiles } from "@/models/UserPersonalFiles";
import { convertBase64ToFile } from "./LazoFileService";

class DocumentCenterFileService {
  private path: string;

  constructor(path: string) {
    this.path = path;
  }

  getByParentIdAndTenantExternalId(
    parentId?: string,
    tenantExternalId?: string,
    lazoUserExternalId?: string
  ): Promise<ApiResponseV2<DocumentCenterFile[]>> {
    let queryStr = "?";
    if (parentId) {
      queryStr += `parentId=${parentId}`;
    }
    if (tenantExternalId) {
      if (queryStr.length !== 1) {
        queryStr += "&";
      }
      queryStr += `tenantExternalId=${tenantExternalId}`;
    }
    if (lazoUserExternalId) {
      if (queryStr.length !== 1) {
        queryStr += "&";
      }
      queryStr += `lazoUserExternalId=${lazoUserExternalId}`;
    }
    queryStr = queryStr.length === 1 ? "" : queryStr;
    return HttpClientV2.get(`${this.path}/files${queryStr}`);
  }

  getByTenantExternalIdAndParentIdPortal(tenantExternalId: string, parentId?: string): Promise<ApiResponseV2<DocumentCenterFile[]>> {
    let queryStr = `?tenantExternalId=${tenantExternalId}`;
    if (parentId) {
      queryStr += `&parentId=${parentId}`;
    }
    return HttpClientV2.get(`${this.path}/files/portal${queryStr}`);
  }

  getPathByExternalId(externalId: string): Promise<ApiResponseV2<DocumentCenterPathStack>> {
    return HttpClientV2.get(`${this.path}/path?externalId=${externalId}`);
  }

  createFolder(
    name: string,
    parentId: string | undefined | null,
    tenantExternalId: string | undefined,
    privacyType: DocumentCenterPrivacyType = DocumentCenterPrivacyType.ALL,
    userExternalId?: string,
    lazoUserExternalId?: string
  ): Promise<ApiResponseV2<DocumentCenterFile>> {
    const body: any = {
      name: name,
      parentId: parentId || null,
      userExternalId: userExternalId || null,
      privacyType: privacyType,
    };

    // Only one of tenantExternalId or lazoUserExternalId should be sent
    if (tenantExternalId) {
      body.tenantExternalId = tenantExternalId;
    } else if (lazoUserExternalId) {
      body.lazoUserExternalId = lazoUserExternalId;
    }

    return HttpClientV2.post(`${this.path}/folder/create`, body);
  }

  rename(externalId: string, name: string): Promise<ApiResponseV2<DocumentCenterFile>> {
    return HttpClientV2.put(`${this.path}/rename`, {
      name,
      externalId,
    });
  }

  getSmallByParentIdAndTypeAndUserAndTenant(
    parentId: string | null | undefined,
    type: GeneralFileNodeType,
    tenantExternalId?: string,
    lazoUserExternalId?: string
  ): Promise<ApiResponseV2<DocumentCenterFileSmall[]>> {
    let queryStr = `?type=${type}`;
    if (parentId) {
      queryStr += `&parentId=${parentId}`;
    }
    if (tenantExternalId) {
      queryStr += `&tenantExternalId=${tenantExternalId}`;
    }
    if (lazoUserExternalId) {
      queryStr += `&lazoUserExternalId=${lazoUserExternalId}`;
    }
    return HttpClientV2.get(`${this.path}/files/small${queryStr}`);
  }

  move(fromExternalIds: string[], destExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/files/move`, {
      fromExternalIds,
      destExternalId,
    });
  }

  moveToCompany(fromExternalIds: string[], targetTenantExternalId: string, destExternalId?: string | null): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/files/portal/move-to-company`, {
      fromExternalIds,
      destExternalId: destExternalId ?? null,
      targetTenantExternalId,
    });
  }

  moveToUser(fromExternalIds: string[], targetLazoUserExternalId: string, destExternalId?: string | null): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/files/portal/move-to-user`, {
      fromExternalIds,
      destExternalId: destExternalId ?? null,
      targetLazoUserExternalId,
    });
  }

  uploadFiles(
    userExternalId: string,
    files: File[],
    tenantExternalId?: string,
    lazoUserExternalId?: string,
    parentId?: string | null,
    overwrittenFiles?: DocumentCenterFile[]
  ): Promise<ApiResponseV2<DocumentCenterFile[]>> {
    const formData = new FormData();
    files.forEach((f) => {
      formData.append("files", f);
    });

    let queryStr = `?userExternalId=${userExternalId}`;
    if (tenantExternalId) {
      queryStr += `&tenantExternalId=${tenantExternalId}`;
    }
    if (lazoUserExternalId) {
      queryStr += `&lazoUserExternalId=${lazoUserExternalId}`;
    }
    if (parentId) {
      queryStr += `&parentId=${parentId}`;
    }
    if (overwrittenFiles?.length > 0) {
      let owIds: string = overwrittenFiles.reduce((ids, f) => (ids += f.externalId + ","), "");
      owIds = owIds.slice(0, owIds.length - 1);
      queryStr += `&overwrittenFiles=${owIds}`;
    }
    return HttpClientV2.post(`${this.path}/files/upload${queryStr}`, formData, {
      axiosConfig: {
        timeout: 300000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    });
  }

  downloadUrl(url: string, filename: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  }

  /**
   * Downloads a single file (returns presigned URL)
   * @deprecated Use downloadItems for unified download handling
   */
  download(fileExternalId: string): Promise<ApiResponseV2<string>> {
    const params = new URLSearchParams();
    params.append("externalIds", fileExternalId);
    return HttpClientV2.get<string>(`${this.path}/files/download`, { axiosConfig: { params } });
  }

  async getPresignedUrlAndDownload(fileExternalId: string, filename: string) {
    const response = await this.download(fileExternalId);
    const presignedUrl = response.getContentOrThrowError();
    this.downloadUrl(presignedUrl, filename);
  }

  /**
   * Downloads multiple items (files and/or folders) as a ZIP
   * @param externalIds Array of external IDs (can be files, folders, or mixed)
   * @returns Blob containing the ZIP file
   */
  async downloadMany(externalIds: string[]): Promise<string | Blob> {
    const responseBlob = await new Promise<string>((resolve, reject) => {
      HttpClientV2.get<string>(`${this.path}/files/download?externalIds=${externalIds.join(",")}`).then((r) => {
        r.onSuccess(async (b) => {
          resolve(b);
        });
        r.onError((e) => {
          const error = new Error(e || "Error downloading files");
          reject(error);
        });
      });
    });
    if (typeof responseBlob === "string" && responseBlob.startsWith("http")) {
      return responseBlob;
    }
    return convertBase64ToFile(responseBlob, "file");
  }

  async downloadItems(externalIds: string[]): Promise<string | Blob> {
    if (externalIds.length === 1) {
      const response = await HttpClientV2.get<string>(`${this.path}/files/download?externalIds=${externalIds[0]}`);

      const content = response.getContentOrThrowError();

      if (typeof content === "string" && content.startsWith("http")) {
        return content;
      }

      return convertBase64ToFile(content, "file");
    }

    return this.downloadMany(externalIds);
  }

  delete(externalIds: string[]): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(
      `${this.path}/files/delete`,
      {
        externalIds,
      },
      {
        axiosConfig: {
          timeout: 60000,
        },
      }
    );
  }

  checkDuplicates(parentId: string, fileNames: string[], tenantExternalId: string): Promise<ApiResponseV2<string[]>> {
    return HttpClientV2.post(`${this.path}/files/check-duplicates`, {
      fileNames,
      parentId,
      tenantExternalId,
    });
  }

  getByDocumentCenterFileExternalId(externalId: string): Promise<ApiResponseV2<DocumentCenterFile>> {
    return HttpClientV2.get(`${this.path}/file/${externalId}`);
  }

  getPreSignedUrl(fileExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/file-pre-signed-url/${fileExternalId}`);
  }

  updatePrivacy(externalId: string, privacyType: DocumentCenterPrivacyType): Promise<ApiResponseV2<String>> {
    return HttpClientV2.put(`${this.path}/privacy`, {
      externalId,
      privacyType,
    });
  }

  // Returns the root nodes of the document center with its file counts
  getTree(tenantExternalId: string): Promise<ApiResponseV2<DocumentCenterNode[]>> {
    return HttpClientV2.get(`${this.path}/tree?tenantExternalId=${tenantExternalId}`);
  }

  getParentIdByFileExternalId(fileExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/parent/${fileExternalId}`);
  }

  getByTerm(
    term: string,
    tenantExternalId: string | undefined,
    lazoUserExternalId: string | undefined,
    isPortal: boolean = false
  ): Promise<ApiResponseV2<Partial<DocumentCenterFile>[]>> {
    const params = new URLSearchParams();
    params.append("term", term);
    params.append("portal", String(isPortal));
    if (tenantExternalId) {
      params.append("tenantExternalId", tenantExternalId);
    }
    if (lazoUserExternalId) {
      params.append("lazoUserExternalId", lazoUserExternalId);
    }
    return HttpClientV2.get(`${this.path}/by-term?${params.toString()}`);
  }

  getInternalDocuments(tenantExternalId: string): Promise<ApiResponseV2<Partial<DocumentCenterFile>[]>> {
    return HttpClientV2.get(`${this.path}/internal-documents?tenantExternalId=${tenantExternalId}`);
  }

  copyTaskFileToDocumentCenter(taskFileExternalId: string, tenantExternalId: string): Promise<ApiResponseV2<DocumentCenterFile>> {
    return HttpClientV2.post(`${this.path}/files/copy-task-file`, {
      taskFileExternalId,
      tenantExternalId,
    });
  }

  copyTaskFileToFolder(
    taskFileExternalId: string,
    tenantExternalId: string,
    targetFolderExternalId: string,
    newFileName?: string
  ): Promise<ApiResponseV2<DocumentCenterFile>> {
    return HttpClientV2.post(`${this.path}/files/copy-task-file-to-folder`, {
      taskFileExternalId,
      tenantExternalId,
      targetFolderExternalId,
      newFileName,
    });
  }

  getUsersPersonalFilesByTenantExternalId(tenantExternalId: string): Promise<ApiResponseV2<UserPersonalFiles[]>> {
    return HttpClientV2.get(`${this.path}/users-personal-files?tenantExternalId=${tenantExternalId}`);
  }
}

export default new DocumentCenterFileService("/api/document-center");
